<script setup lang="ts">
import {
	useCurveModelStore,
	useCurveModelChartStore,
} from "@/modules/booking_experts/store";
import useDialog from "@/ui/composables/useDialog";
import OffsetCurveItemComponent from "./OffsetCurveItemComponent.vue";
import PriceModelCurveOffsetDO = Modules.BookingExperts.Data.PriceModelCurve.PriceModelCurveOffsetDO;
import { computed } from "vue";

/* Props */
const { isReadonly = false } = defineProps<{
	/** Is this chart somehow editable? */
	isReadonly?: boolean;
}>();

const dialog = useDialog();
const store = useCurveModelStore();
const chartStore = useCurveModelChartStore();

function addOffset(isPositive: boolean) {
	const value = isPositive ? 5 : -5;
	const priceAdjustmentValue =
		store.currentCurve.price_adjustment_type === "absolute" ? 0 : value;
	store.currentCurve = {
		...store.currentCurve,
		offsets: [
			...store.currentCurve.offsets,
			store.currentCurve.price_adjustment_type === "absolute"
				? { offsetPercentage: value, priceAbsoluteChange: priceAdjustmentValue }
				: {
						offsetPercentage: value,
						pricePercentageChange: priceAdjustmentValue,
				  },
		],
	};
}

async function deleteAllOffsets(isPositive: boolean) {
	const choice = await dialog.confirm({
		title: `Delete all ${isPositive ? "positive" : "negative"} offsets`,
		message: `Are you sure you want to delete all ${
			isPositive ? "positive" : "negative"
		} offsets?`,
		isDelete: true,
	});

	if (!choice) return;

	store.currentCurve = {
		...store.currentCurve,
		offsets: store.currentCurve.offsets.filter((offset) =>
			isPositive ? offset.offsetPercentage <= 0 : offset.offsetPercentage >= 0
		),
	};
}

function updateOffsets(newOffsets: PriceModelCurveOffsetDO[]) {
	store.currentCurve = {
		...store.currentCurve,
		offsets: newOffsets,
	};
}

function handleHover(isPositive: boolean, index: number, isEntering: boolean) {
	const targetIndex = isEntering ? index : null;
	if (isPositive) {
		chartStore.highlightedPositiveIndex = targetIndex;
	} else {
		chartStore.highlightedNegativeIndex = targetIndex;
	}
}

const hasOffsets = computed(() => store.currentCurve.offsets.length > 0);
</script>

<template>
	<div class="offset-grid">
		<!-- Positive Offsets -->
		<div>
			<h3 class="d-flex align-center ga-2 mb-2">
				Positive Offsets
				<v-spacer />
				<template v-if="!isReadonly">
					<v-btn
						size="30"
						variant="text"
						v-tooltip="'Add positive offset'"
						@click="addOffset(true)"
						data-testid="add-positive-offset"
					>
						<i class="fa-light fa-plus" />
					</v-btn>
					<v-btn
						size="30"
						variant="text"
						color="error"
						v-tooltip="'Delete all positive offsets'"
						:disabled="!hasOffsets"
						@click="deleteAllOffsets(true)"
						data-testid="delete-all-positive-offsets"
					>
						<i class="fa-light fa-trash-can text-error" />
					</v-btn>
				</template>
			</h3>
			<OffsetCurveItemComponent
				:isReadonly="isReadonly"
				:offsets="store.currentCurve.offsets"
				:price-adjustment-type="store.currentCurve.price_adjustment_type"
				:isPositive="true"
				@update:offsets="updateOffsets"
				@hover="(index, isEntering) => handleHover(true, index, isEntering)"
			/>
		</div>

		<!-- Negative Offsets -->
		<div>
			<h3 class="d-flex align-center ga-2 mb-2">
				Negative Offsets
				<v-spacer />
				<template v-if="!isReadonly">
					<v-btn
						size="30"
						variant="text"
						v-tooltip="'Add negative offset'"
						@click="addOffset(false)"
						data-testid="add-negative-offset"
					>
						<i class="fa-light fa-plus" />
					</v-btn>
					<v-btn
						size="30"
						variant="text"
						color="error"
						v-tooltip="'Delete all negative offsets'"
						:disabled="!hasOffsets"
						@click="deleteAllOffsets(false)"
					>
						<i class="fa-light fa-trash-can text-error" />
					</v-btn>
				</template>
			</h3>

			<!-- <v-alert
        v-if="isOccupancyType"
        type="info"
        variant="outlined"
        title="Negative offsets unavailable"
      >
        Occupancy curves only support positive offsets since the base occupancy
        level typically starts below the target level.
      </v-alert> -->

			<OffsetCurveItemComponent
				:isReadonly="isReadonly"
				:offsets="store.currentCurve.offsets"
				:price-adjustment-type="store.currentCurve.price_adjustment_type"
				:isPositive="false"
				@update:offsets="updateOffsets"
				@hover="(index, isEntering) => handleHover(false, index, isEntering)"
			/>
		</div>
	</div>
</template>

<style scoped>
.offset-grid {
	display: grid;
	gap: 1rem;
	grid-template-columns: 1fr 1fr;
}
</style>
